import { content, type Language } from '../config/siteContent'
import { routeFor } from '../config/routes'
import { siteConfig, type PricingCategory } from '../config/site'
import { StaggerGroup } from './motion'
import PricingValue from './PricingValue'
import { ButtonLink, SectionHeading } from './ui'

type PricingSectionProps = { language: Language }

export default function PricingSection({ language }: PricingSectionProps) {
  if (!siteConfig.showPricing) return null

  const copy = content[language]
  const categories = siteConfig.pricing.categories as readonly PricingCategory[]
  const contactPath = `${routeFor(language, 'contact')}#contact-form`
  const packageNames = [copy.pricing.starter, copy.pricing.standard, copy.pricing.advanced]
  const includesLabel = language === 'el' ? 'Ένα ολοκληρωμένο AI persona μπορεί να περιλαμβάνει:' : 'A complete AI persona may include:'
  const additionalServicesLabel = language === 'el' ? 'Επιπλέον υπηρεσίες ανάπτυξης' : 'Additional development services'

  return (
    <section className="section pricing-section" id="pricing" aria-labelledby="pricing-title">
      <div className="shell">
        <SectionHeading eyebrow={copy.services.pricingEyebrow} title={copy.services.pricingTitle} text={copy.services.pricingIntro} />
        <StaggerGroup className="pricing-catalog">
          {categories.map((category, index) => (
            <article className={`pricing-category pricing-category--${category.id}`} id={`pricing-${category.id}`} key={category.id}>
              <header className="pricing-category__header">
                <span>0{index + 1}</span>
                <h3>{category.title[language]}</h3>
              </header>
              {category.id === 'websites' ? (
                <>
                  <div className="pricing-package-grid">
                    {category.items.slice(0, 3).map((item, itemIndex) => (
                      <article className={`pricing-package pricing-package--${itemIndex === 0 ? 'starter' : itemIndex === 1 ? 'standard' : 'advanced'}`} data-pricing-item={item.id} key={item.id}>
                        <p className="pricing-package__tier">{packageNames[itemIndex]}</p>
                        <h4>{item.label[language]}</h4>
                        <PricingValue language={language} value={item.price} />
                        <ButtonLink to={contactPath} variant={itemIndex === 1 ? 'solid' : 'outline'}>{copy.common.requestQuote}</ButtonLink>
                      </article>
                    ))}
                  </div>
                  <div className="pricing-category__additional">
                    <h4>{additionalServicesLabel}</h4>
                    <ul className="pricing-category__items pricing-category__items--additional">
                      {category.items.slice(3).map((item) => (
                        <li data-pricing-item={item.id} key={item.id}>
                          <span>{item.label[language]}</span>
                          <PricingValue language={language} value={item.price} />
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <ul className="pricing-category__items">
                  {category.items.map((item) => (
                    <li data-pricing-item={item.id} key={item.id}>
                      <span>{item.label[language]}</span>
                      <PricingValue language={language} value={item.price} />
                    </li>
                  ))}
                </ul>
              )}
              {category.includes && (
                <div className="pricing-category__includes">
                  <p>{includesLabel}</p>
                  <ul>{category.includes.map((item) => <li key={item[language]}>{item[language]}</li>)}</ul>
                </div>
              )}
              {category.id !== 'websites' && <ButtonLink className="pricing-category__cta" to={contactPath} variant="text">{copy.common.requestQuote}</ButtonLink>}
            </article>
          ))}
        </StaggerGroup>
        <p className="pricing-note">{siteConfig.pricing.disclaimer[language]}</p>
      </div>
    </section>
  )
}
